/**
 * TeamTab.jsx — Settings Team tab
 *
 * Features:
 * - List roster members with Core/Extended badge
 * - Add new member form (name, crewId, isCore)
 * - Crew rate management (per-member hourly rates by category)
 * - Permission assignment via embedded PermissionsPanel
 * - Deduplication warning for duplicate entries
 */

import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRoster, useAddToRoster, useUpdateRoster, useRemoveFromRoster } from '../../hooks/useRoster';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { useToast } from '../layout/Toast';
import UserAvatar from '../shared/UserAvatar';
import { getUserColor } from '../../lib/userColors';
import { cn } from '../../lib/utils';
import { RATE_CATEGORIES } from '../../lib/crewConstants';
import { useRBAC } from '../../hooks/useRBAC';
import { useAuth } from '../../hooks/useAuth';
import { useProfiles } from '../../hooks/useProfiles';
import { useFocus } from '../../hooks/useFocus';
import FocusSetsPanel from './FocusSetsPanel';

const ROLE_OPTIONS = [
  { id: 'super_admin', label: 'Super Admin', color: '#DC2626', desc: 'Full access to everything including permissions & data export' },
  { id: 'admin',       label: 'Admin',       color: '#F59E0B', desc: 'Full operations access, no permission management' },
  { id: 'core_crew',   label: 'Core Crew',   color: '#3B82F6', desc: 'Regular team — most features, limited financials' },
  { id: 'crew',        label: 'Crew',        color: '#6B7280', desc: 'Freelancers — assigned jobs only, own pay' },
];

// Parse roster name (handles V1 nested JSON)
function parseRosterName(raw) {
  if (!raw || typeof raw !== 'string') return raw || 'Unknown';
  let parsed = raw;
  for (let i = 0; i < 3; i++) {
    try {
      const attempt = JSON.parse(parsed);
      if (typeof attempt === 'object' && attempt !== null && attempt.name) {
        parsed = attempt.name;
      } else if (typeof attempt === 'string') {
        parsed = attempt;
      } else { break; }
    } catch { break; }
  }
  return parsed;
}

function parseCrewId(member) {
  if (member.crewId) return member.crewId;
  try {
    const parsed = JSON.parse(member.name);
    if (parsed?.crewId) return parsed.crewId;
  } catch {}
  return null;
}

export default function TeamTab() {
  const { data: roster = [], isLoading } = useRoster();
  const { members: focusMembers } = useFocus();
  const addToRoster = useAddToRoster();
  const updateRoster = useUpdateRoster();
  const removeFromRoster = useRemoveFromRoster();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { toast } = useToast();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [activeSection, setActiveSection] = useState('roster'); // roster | rates
  const [editingRates, setEditingRates] = useState({});
  const [selectedMemberForRates, setSelectedMemberForRates] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Detect duplicates
  const duplicates = useMemo(() => {
    const nameMap = {};
    roster.forEach((m) => {
      const name = parseRosterName(m.name);
      nameMap[name] = (nameMap[name] || 0) + 1;
    });
    return Object.entries(nameMap)
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
  }, [roster]);

  // ── Add Member ──────────────────────────────────────────────────────────
  const handleAddMember = async () => {
    if (!newName.trim()) {
      toast('Name is required', 'error');
      return;
    }
    const crewId = `crm_${Date.now().toString(36)}`;
    try {
      await addToRoster.mutateAsync({
        name: newName.trim(),
        crewId,
        role: newTitle.trim() || null,
        email: newEmail.trim() || null,
        phone: newPhone.trim() || null,
        isCore: false,
      });
      toast('Member added to roster');
      setNewName('');
      setNewTitle('');
      setNewEmail('');
      setNewPhone('');
      setShowAddForm(false);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Remove Member ───────────────────────────────────────────────────────
  const handleRemove = async (member) => {
    try {
      await removeFromRoster.mutateAsync(member.id);
      toast('Removed from roster');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Edit Member ─────────────────────────────────────────────────────────
  const handleStartEdit = (member) => {
    setEditingMemberId(member.id);
    setEditForm({
      name: parseRosterName(member.name),
      title: member.role || '',
      email: member.email || '',
      phone: member.phone || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (memberId) => {
    if (!editForm.name?.trim()) {
      toast('Name is required', 'error');
      return;
    }
    try {
      await updateRoster.mutateAsync({
        id: memberId,
        name: editForm.name.trim(),
        role: editForm.title.trim() || null,
        email: editForm.email.trim() || null,
        phone: editForm.phone.trim() || null,
      });
      toast('Member updated');
      setEditingMemberId(null);
      setEditForm({});
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Dedup ───────────────────────────────────────────────────────────────
  const handleDedup = async () => {
    // For each duplicate name, keep the first entry and remove the rest
    const seen = {};
    const toRemove = [];
    roster.forEach((member) => {
      const name = parseRosterName(member.name);
      if (seen[name]) {
        toRemove.push(member.id);
      } else {
        seen[name] = true;
      }
    });

    try {
      for (const id of toRemove) {
        await removeFromRoster.mutateAsync(id);
      }
      toast(`Removed ${toRemove.length} duplicate entries`);
    } catch (err) {
      toast(`Error removing duplicates: ${err.message}`, 'error');
    }
  };

  // ── Crew Rates ──────────────────────────────────────────────────────────
  const handleEditRates = (crewId) => {
    setSelectedMemberForRates(crewId);
    const rates = settings?.data?.crewRates?.[crewId] || {};
    setEditingRates(rates);
  };

  const handleSaveRates = async () => {
    if (!selectedMemberForRates) return;
    try {
      await updateSettings.mutateAsync({
        ...settings,
        data: {
          ...settings.data,
          crewRates: {
            ...(settings.data.crewRates || {}),
            [selectedMemberForRates]: editingRates,
          },
        },
      });
      toast('Crew rates saved');
      setSelectedMemberForRates(null);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  if (isLoading) {
    return <div className="text-text-dim text-sm p-4">Loading team...</div>;
  }

  const sections = [
    { id: 'roster', label: 'Roster' },
    { id: 'rates', label: 'Crew Rates' },
    // The focus API only returns everyone's sets to a settings:full caller.
    ...(focusMembers ? [{ id: 'focus', label: 'Focus Sets' }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-2">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded transition-colors',
              activeSection === s.id
                ? 'bg-accent text-bg'
                : 'bg-surface border border-border text-text-dim hover:text-text-primary'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Duplicate Warning */}
      {duplicates.length > 0 && (
        <div className="p-3 bg-[#A47B1A]/10 border border-[#A47B1A]/30 rounded text-sm text-[#A47B1A] flex items-center justify-between">
          <div>
            <span className="font-semibold">Duplicate entries detected:</span>{' '}
            {duplicates.join(', ')}.
          </div>
          <button
            onClick={handleDedup}
            disabled={removeFromRoster.isPending}
            className="px-3 py-1.5 bg-[#A47B1A] text-white rounded text-xs font-medium hover:bg-[#A47B1A]/80 disabled:opacity-50 transition-colors ml-3 flex-shrink-0"
          >
            Remove Duplicates
          </button>
        </div>
      )}

      {/* ROSTER SECTION */}
      {activeSection === 'roster' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text-primary">
              Roster Members ({roster.length})
            </h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-3 py-1.5 bg-accent text-bg rounded text-xs font-medium hover:bg-accent-hover transition-colors"
            >
              {showAddForm ? 'Cancel' : '+ Add Member'}
            </button>
          </div>

          {/* Add Member Form */}
          {showAddForm && (
            <div className="p-4 bg-surface border border-accent/30 rounded space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Name *</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Full name"
                    className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Title</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="e.g., Pilot, Editor, Producer"
                    className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="crew@email.com"
                    className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="(555) 555-0000"
                    className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleAddMember}
                  disabled={addToRoster.isPending}
                  className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
                >
                  {addToRoster.isPending ? 'Adding...' : 'Add to Roster'}
                </button>
              </div>
            </div>
          )}

          {/* Roster List */}
          {roster.length === 0 ? (
            <div className="text-text-dim text-sm p-4 bg-surface rounded border border-border">
              No team members yet. Add your first member above.
            </div>
          ) : (
            <div className="space-y-2">
              {roster.map((member) => {
                const name = parseRosterName(member.name);
                const isDuplicate = duplicates.includes(name);
                const isEditing = editingMemberId === member.id;

                if (isEditing) {
                  return (
                    <div key={member.id} className="p-4 bg-surface border border-accent/30 rounded space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-text-dim mb-1">Name *</label>
                          <input type="text" value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-dim mb-1">Title</label>
                          <input type="text" value={editForm.title} onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                            placeholder="e.g., Pilot, Editor, Producer"
                            className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-text-dim mb-1">Email</label>
                          <input type="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-dim mb-1">Phone</label>
                          <input type="tel" value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                            className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent" />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={handleCancelEdit}
                          className="px-3 py-1.5 text-xs font-medium text-text-dim border border-border rounded hover:bg-surface-2 transition-colors">
                          Cancel
                        </button>
                        <button onClick={() => handleSaveEdit(member.id)} disabled={updateRoster.isPending}
                          className="px-4 py-1.5 bg-accent text-bg rounded text-xs font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors">
                          {updateRoster.isPending ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={member.id}
                    className={cn(
                      'flex items-center justify-between gap-2 flex-wrap p-3 bg-surface border rounded transition-colors',
                      isDuplicate ? 'border-[#A47B1A]/40' : 'border-border'
                    )}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <UserAvatar name={name} color={getUserColor(member)} size="sm" />
                        <span className="font-medium text-text-primary text-sm">{name}</span>
                        {member.role && (
                          <span className="text-2xs text-text-dim">· {member.role}</span>
                        )}
                        {isDuplicate && (
                          <span className="text-2xs px-1.5 py-0.5 rounded bg-[#A47B1A]/10 border border-[#A47B1A]/30 text-[#A47B1A]">
                            Duplicate
                          </span>
                        )}
                      </div>
                      <div className="text-text-dim text-xs mt-0.5 flex gap-3">
                        {member.email && <span>{member.email}</span>}
                        {member.phone && <span>{member.phone}</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <MemberRoleBadge member={member} />
                      <button
                        onClick={() => handleStartEdit(member)}
                        className="text-accent hover:text-accent-hover text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemove(member)}
                        className="text-red-accent hover:text-red-500 text-xs font-medium"
                        disabled={removeFromRoster.isPending}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CREW RATES SECTION */}
      {activeSection === 'focus' && focusMembers && <FocusSetsPanel />}

      {activeSection === 'rates' && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">Crew Hourly Rates</h3>
          <p className="text-text-dim text-xs">Set per-member hourly rates by work category. Used in payroll calculations.</p>

          {roster.length === 0 ? (
            <div className="text-text-dim text-sm p-4 bg-surface rounded border border-border">
              Add members to the roster first.
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-4">
              {/* Member list */}
              <div className="w-full md:w-1/4 space-y-1">
                {roster.map((member) => {
                  const crewId = parseCrewId(member);
                  return (
                    <button
                      key={member.id}
                      onClick={() => handleEditRates(crewId)}
                      className={cn(
                        'w-full px-3 py-2 rounded text-left text-sm transition-colors',
                        selectedMemberForRates === crewId
                          ? 'bg-accent text-bg font-medium'
                          : 'bg-surface border border-border text-text-primary hover:border-accent'
                      )}
                    >
                      {parseRosterName(member.name)}
                    </button>
                  );
                })}
              </div>

              {/* Rate editor */}
              <div className="flex-1">
                {selectedMemberForRates ? (
                  <div className="space-y-3 p-4 bg-surface border border-border rounded">
                    {RATE_CATEGORIES.map((cat) => (
                      <div key={cat}>
                        <label className="block text-xs font-medium text-text-dim mb-1 capitalize">
                          {cat} ($/hr)
                        </label>
                        <input
                          type="number"
                          value={editingRates[cat] || ''}
                          onChange={(e) =>
                            setEditingRates((prev) => ({ ...prev, [cat]: parseFloat(e.target.value) || 0 }))
                          }
                          step="5"
                          min="0"
                          className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                        />
                      </div>
                    ))}
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={handleSaveRates}
                        disabled={updateSettings.isPending}
                        className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
                      >
                        {updateSettings.isPending ? 'Saving...' : 'Save Rates'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-text-dim text-sm p-4 bg-surface rounded border border-border">
                    Select a member to edit their rates.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ── Inline role badge / invite per roster member ─────────────────────────

function MemberRoleBadge({ member }) {
  const { isSuperAdmin } = useRBAC();
  const { profile } = useAuth();
  const { data: profiles = [] } = useProfiles();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteRole, setInviteRole] = useState('crew');

  const prof = profiles.find(p => p.id === member.profileId);

  // Has portal account → role dropdown (super_admin) or badge
  if (member.profileId && prof) {
    const currentRole = prof.role || 'crew';
    const roleInfo = ROLE_OPTIONS.find(r => r.id === currentRole);

    if (isSuperAdmin) {
      return (
        <select
          value={currentRole}
          onChange={async (e) => {
            const newRole = e.target.value;
            if (member.profileId === profile.id && newRole !== 'super_admin') {
              toast('Cannot demote yourself', 'error');
              return;
            }
            setSaving(true);
            try {
              const res = await fetch(`/api/profiles/${member.profileId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ role: newRole }),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Failed');
              }
              toast(`Role → ${newRole.replace(/_/g, ' ')}`, 'success');
              qc.invalidateQueries({ queryKey: ['profiles'] });
            } catch (e) {
              toast(e.message, 'error');
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          className="px-2 py-1 bg-bg border border-border rounded text-xs text-text-primary focus:outline-none focus:border-accent"
          style={{ borderColor: roleInfo?.color + '40' }}
        >
          {ROLE_OPTIONS.map(r => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
      );
    }

    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium"
        style={{ color: roleInfo?.color, backgroundColor: roleInfo?.color + '15', border: `1px solid ${roleInfo?.color}30` }}
      >
        {roleInfo?.label || currentRole}
      </span>
    );
  }

  // No portal account → super_admin sees invite controls
  if (isSuperAdmin) {
    return (
      <div className="flex items-center gap-1">
        <select
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value)}
          className="px-1.5 py-1 bg-bg border border-border rounded text-2xs text-text-primary focus:outline-none focus:border-accent"
        >
          {ROLE_OPTIONS.map(r => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
        <button
          onClick={async () => {
            if (!member.email) {
              toast('Add an email first to invite this member.', 'error');
              return;
            }
            setInviting(true);
            try {
              const res = await fetch('/api/auth/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  email: member.email,
                  full_name: parseRosterName(member.name),
                  role: inviteRole,
                  roster_id: member.id,
                }),
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Failed');
              toast(`Invited ${parseRosterName(member.name)}`, 'success');
              qc.invalidateQueries({ queryKey: ['profiles'] });
              qc.invalidateQueries({ queryKey: ['roster'] });
            } catch (e) {
              toast(e.message, 'error');
            } finally {
              setInviting(false);
            }
          }}
          disabled={inviting || !member.email}
          className="px-2 py-1 bg-accent text-bg rounded text-2xs font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
        >
          {inviting ? '...' : 'Invite'}
        </button>
      </div>
    );
  }

  return <span className="text-xs text-text-dim/50 italic">No account</span>;
}
